import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 健康医学 - 横屏「人体图解」风格
 *
 * 重构版：更大气的布局、更精致的人体轮廓、无文字重叠
 *
 * 布局：左侧大面积人体轮廓 + 右侧信息面板
 * 标注线从器官引出到右侧固定行高位置，彻底避免重叠
 *
 * 尺寸：1920x1080 横屏
 */

interface OrganInfo {
  name: string;
  icon: string;
  bodyPos: { x: number; y: number };
  fact: string;
  color: string;
}

interface HealthMetric {
  label: string;
  value: number;
  max: number;
  unit: string;
  status: 'good' | 'warning' | 'danger';
}

interface HealthAnatomyTemplateProps {
  topic: string;
  subtitle: string;
  organs: OrganInfo[];
  metrics: HealthMetric[];
  keyFact: string;
  tips: string[];
  source: string;
}

const DEFAULT_PROPS: HealthAnatomyTemplateProps = {
  topic: '人体心血管系统',
  subtitle: 'Cardiovascular System Overview',
  organs: [
    { name: '大脑', icon: '🧠', bodyPos: { x: 400, y: 105 }, fact: '接收心脏 20% 的血液供应，重仅 1.4kg 却消耗全身 20% 氧气', color: '#818cf8' },
    { name: '肺', icon: '🫁', bodyPos: { x: 340, y: 300 }, fact: '肺泡展开面积约 70m²，相当于一个网球场的大小', color: '#f472b6' },
    { name: '心脏', icon: '❤️', bodyPos: { x: 430, y: 340 }, fact: '每天跳动约 10 万次，泵出约 7,500 升血液', color: '#ef4444' },
    { name: '肝脏', icon: '🟤', bodyPos: { x: 350, y: 440 }, fact: '人体最大内脏器官，具有 500+ 种代谢与解毒功能', color: '#b45309' },
    { name: '肾脏', icon: '🫘', bodyPos: { x: 440, y: 480 }, fact: '每天过滤约 180 升血液，维持体内电解质平衡', color: '#0891b2' },
  ],
  metrics: [
    { label: '心率', value: 72, max: 100, unit: 'BPM', status: 'good' },
    { label: '血压', value: 120, max: 140, unit: 'mmHg', status: 'good' },
    { label: '血氧', value: 98, max: 100, unit: '%', status: 'good' },
    { label: '胆固醇', value: 5.2, max: 6.2, unit: 'mmol/L', status: 'warning' },
  ],
  keyFact: '心脏一生中大约跳动 30 亿次——它是人体中最不知疲倦的器官',
  tips: [
    '每周 150 分钟中等强度有氧运动',
    '控制钠摄入 < 5g/天',
    '保持 BMI 在 18.5-24.9 之间',
    '每年至少一次心血管体检',
  ],
  source: 'WHO Cardiovascular Guidelines 2024',
};

const CircularProgress: React.FC<{
  value: number;
  max: number;
  color: string;
  size: number;
  frame: number;
  delay: number;
}> = ({ value, max, color, size, frame, delay }) => {
  const progress = interpolate(frame, [delay, delay + 40], [0, value / max], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const r = (size - 10) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - progress);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90, ${size / 2}, ${size / 2})`}
      />
    </svg>
  );
};

export const HealthAnatomyTemplate: React.FC<Partial<HealthAnatomyTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const bodyDraw = interpolate(frame, [15, 70], [0, 1], { extrapolateRight: 'clamp' });

  const organStart = 60;
  const organInterval = 50;

  const metricStart = organStart + p.organs.length * organInterval + 20;
  const factStart = metricStart + 100;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });
  const tipsStart = factStart + 40;

  const heartScale = 1 + 0.12 * Math.abs(Math.sin(frame * 0.12));

  const statusColorMap = { good: '#22c55e', warning: '#f59e0b', danger: '#ef4444' };

  // Right-side label layout: fixed Y positions with enough spacing
  const labelBaseX = 780;
  const labelYPositions = [280, 400, 510, 620, 730];

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(160deg, #f0fdf4 0%, #f8fafc 40%, #ecfdf5 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#0f172a',
        overflow: 'hidden',
      }}
    >
      {/* Subtle background pattern */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, #16a34a08 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      {/* Decorative top gradient bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #22c55e, #16a34a, #059669)' }} />

      {/* ========== HEADER ========== */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          left: 60,
          opacity: headerProgress,
          transform: `translateY(${(1 - headerProgress) * -10}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          <div
            style={{
              padding: '8px 20px',
              background: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: 12,
              fontSize: 18,
              color: '#16a34a',
              fontWeight: 700,
              letterSpacing: '0.06em',
            }}
          >
            🏥 HEALTH
          </div>
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>{p.topic}</h1>
        <p style={{ fontSize: 26, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== BODY SILHOUETTE (SVG) ========== */}
      <svg
        style={{ position: 'absolute', left: 40, top: 240 }}
        width="760"
        height="820"
        viewBox="50 40 700 860"
      >
        {/* Head */}
        <ellipse
          cx="400" cy="100" rx="52" ry="62"
          fill="none" stroke="#94a3b8" strokeWidth="2.5"
          strokeDasharray="500" strokeDashoffset={500 * (1 - bodyDraw)}
          opacity="0.18"
        />
        {/* Neck */}
        <path
          d="M382,160 L382,190 M418,160 L418,190"
          fill="none" stroke="#94a3b8" strokeWidth="2.5"
          strokeDasharray="100" strokeDashoffset={100 * (1 - bodyDraw)}
          opacity="0.15"
        />
        {/* Shoulders + torso */}
        <path
          d="M382,190 Q370,195 280,220 Q250,230 240,260 L240,280
             M418,190 Q430,195 520,220 Q550,230 560,260 L560,280"
          fill="none" stroke="#94a3b8" strokeWidth="2.5"
          strokeDasharray="800" strokeDashoffset={800 * (1 - bodyDraw)}
          opacity="0.15"
        />
        {/* Torso body */}
        <path
          d="M270,260 Q265,360 280,460 Q290,530 320,570 Q350,600 400,610 Q450,600 480,570 Q510,530 520,460 Q535,360 530,260"
          fill="none" stroke="#94a3b8" strokeWidth="2.5"
          strokeDasharray="1200" strokeDashoffset={1200 * (1 - bodyDraw)}
          opacity="0.15"
        />
        {/* Inner torso fill gradient */}
        <path
          d="M280,260 Q275,360 290,460 Q300,530 330,570 Q360,600 400,610 Q440,600 470,570 Q500,530 510,460 Q525,360 520,260 Z"
          fill="#16a34a" opacity={bodyDraw * 0.025}
        />
        {/* Arms */}
        <path
          d="M240,280 Q220,340 200,400 Q185,440 180,480
             M560,280 Q580,340 600,400 Q615,440 620,480"
          fill="none" stroke="#94a3b8" strokeWidth="2"
          strokeDasharray="600" strokeDashoffset={600 * (1 - bodyDraw)}
          opacity="0.12"
        />
        {/* Hands */}
        <ellipse cx="175" cy="495" rx="18" ry="24" fill="none" stroke="#94a3b8" strokeWidth="1.5" opacity={bodyDraw * 0.12} />
        <ellipse cx="625" cy="495" rx="18" ry="24" fill="none" stroke="#94a3b8" strokeWidth="1.5" opacity={bodyDraw * 0.12} />
        {/* Legs */}
        <path
          d="M340,600 Q330,670 320,740 Q315,790 310,840
             M460,600 Q470,670 480,740 Q485,790 490,840"
          fill="none" stroke="#94a3b8" strokeWidth="2.5"
          strokeDasharray="700" strokeDashoffset={700 * (1 - bodyDraw)}
          opacity="0.12"
        />

        {/* ========== Organ markers + connector lines ========== */}
        {p.organs.map((organ, i) => {
          const start = organStart + i * organInterval;
          const progress = spring({
            frame: frame - start,
            fps,
            from: 0,
            to: 1,
            config: { damping: 12, stiffness: 130, mass: 0.5 },
          });
          if (progress < 0.01) return null;

          const isHeart = organ.name === '心脏';
          const scale = isHeart ? heartScale : 1;
          const labelY = labelYPositions[i] ?? (130 + i * 140);

          return (
            <g key={i} opacity={progress}>
              {/* Connector: bezier curve from organ to right-side label */}
              <path
                d={`M${organ.bodyPos.x + 18},${organ.bodyPos.y} Q${organ.bodyPos.x + 120},${organ.bodyPos.y} ${labelBaseX - 20},${labelY + 20}`}
                fill="none"
                stroke={organ.color}
                strokeWidth="1.5"
                strokeDasharray="6 4"
                opacity="0.35"
              />
              <circle cx={labelBaseX - 20} cy={labelY + 20} r="3" fill={organ.color} opacity="0.5" />

              {/* Organ circle on body */}
              <circle
                cx={organ.bodyPos.x}
                cy={organ.bodyPos.y}
                r={20 * scale}
                fill={`${organ.color}10`}
                stroke={organ.color}
                strokeWidth="2.5"
              />

              {/* Pulse ring (heart only) */}
              {isHeart && (
                <>
                  <circle cx={organ.bodyPos.x} cy={organ.bodyPos.y} r={28 * heartScale} fill="none" stroke={organ.color} strokeWidth="1.5" opacity={0.15 / heartScale} />
                  <circle cx={organ.bodyPos.x} cy={organ.bodyPos.y} r={36 * heartScale} fill="none" stroke={organ.color} strokeWidth="1" opacity={0.08 / heartScale} />
                </>
              )}
            </g>
          );
        })}
      </svg>

      {/* ========== ORGAN ICON on body (HTML for emoji rendering) ========== */}
      {p.organs.map((organ, i) => {
        const start = organStart + i * organInterval;
        const progress = spring({
          frame: frame - start,
          fps,
          from: 0,
          to: 1,
          config: { damping: 12, stiffness: 130, mass: 0.5 },
        });
        if (progress < 0.05) return null;

        const svgOffsetX = 40;
        const svgOffsetY = 200;
        const svgScaleX = 760 / 700;
        const svgScaleY = 820 / 860;

        return (
          <div
            key={`icon-${i}`}
            style={{
              position: 'absolute',
              left: svgOffsetX + (organ.bodyPos.x - 50) * svgScaleX - 14,
              top: svgOffsetY + (organ.bodyPos.y - 40) * svgScaleY - 14,
              width: 28,
              height: 28,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: 22,
              opacity: progress,
              pointerEvents: 'none',
            }}
          >
            {organ.icon}
          </div>
        );
      })}

      {/* ========== RIGHT-SIDE ORGAN LABELS (no overlap) ========== */}
      {p.organs.map((organ, i) => {
        const start = organStart + i * organInterval;
        const progress = spring({
          frame: frame - start,
          fps,
          from: 0,
          to: 1,
          config: { damping: 14, stiffness: 110, mass: 0.5 },
        });
        if (progress < 0.05) return null;

        const labelY = labelYPositions[i] ?? (130 + i * 140);

        return (
          <div
            key={`label-${i}`}
            style={{
              position: 'absolute',
              left: labelBaseX,
              top: labelY,
              width: 340,
              opacity: progress,
              transform: `translateX(${(1 - progress) * 20}px)`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: organ.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 26, fontWeight: 800, color: organ.color }}>{organ.name}</span>
            </div>
            <div style={{ fontSize: 20, color: '#475569', lineHeight: 1.6, paddingLeft: 18 }}>
              {organ.fact}
            </div>
          </div>
        );
      })}

      {/* ========== HEALTH METRICS + TIPS (top-right) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 270,
          right: 60,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 16,
        }}
      >
        <div style={{ fontSize: 26, color: '#16a34a', letterSpacing: '0.1em', fontWeight: 800 }}>
          📊 健康指标
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {p.metrics.map((m, i) => {
            const start = metricStart + i * 20;
            const progress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 12, stiffness: 140, mass: 0.5 },
            });
            const color = statusColorMap[m.status];
            const displayValue = interpolate(frame, [start, start + 35], [0, m.value], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

            return (
              <div
                key={i}
                style={{
                  padding: '18px 22px',
                  background: 'white',
                  borderRadius: 20,
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  opacity: progress,
                  textAlign: 'center',
                  width: 140,
                }}
              >
                <div style={{ position: 'relative', width: 90, height: 90, margin: '0 auto' }}>
                  <CircularProgress value={m.value} max={m.max} color={color} size={90} frame={frame} delay={start} />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: 28,
                      fontWeight: 900,
                      color,
                    }}
                  >
                    {m.value % 1 !== 0 ? displayValue.toFixed(1) : Math.round(displayValue)}
                  </div>
                </div>
                <div style={{ fontSize: 22, color: '#334155', marginTop: 10, fontWeight: 700 }}>{m.label}</div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>{m.unit}</div>
              </div>
            );
          })}
        </div>

        {/* ========== 健康建议 (below metrics) ========== */}
        <div
          style={{
            width: '100%',
            padding: '20px 26px',
            background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
            borderRadius: 20,
            border: '1px solid #bbf7d0',
            marginTop: 4,
            opacity: factOpacity,
          }}
        >
          <div style={{ fontSize: 26, color: '#16a34a', marginBottom: 14, fontWeight: 800 }}>🎯 健康建议</div>
          {p.tips.map((tip, i) => {
            const start = tipsStart + i * 22;
            const opacity = interpolate(frame, [start, start + 18], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12, opacity }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', marginTop: 10, flexShrink: 0 }} />
                <span style={{ fontSize: 24, color: '#334155', lineHeight: 1.5, fontWeight: 500 }}>{tip}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== BOTTOM BAR: Key Fact ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 60,
          right: 60,
        }}
      >
        <div
          style={{
            padding: '24px 32px',
            background: 'linear-gradient(135deg, #dcfce7, #d1fae5)',
            borderRadius: 20,
            opacity: factOpacity,
          }}
        >
          <div style={{ fontSize: 26, color: '#16a34a', fontWeight: 700, marginBottom: 10 }}>❤️ 核心知识</div>
          <div style={{ fontSize: 24, color: '#0f172a', lineHeight: 1.6, fontWeight: 500 }}>{p.keyFact}</div>
        </div>
      </div>

      {/* Source */}
      <div
        style={{
          position: 'absolute',
          bottom: 14,
          right: 60,
          fontSize: 14,
          color: '#94a3b8',
          opacity: factOpacity * 0.5,
        }}
      >
        📎 {p.source}
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, durationInFrames], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #22c55e, #16a34a)',
        }}
      />
    </AbsoluteFill>
  );
};
